import React, { useEffect, useState } from 'react';
import FormModal from '../form/FormModal';
import I18n from '../i18n/I18n';
import { useStyling } from '../Theme';
import type { SavePreferenceFormData, SavePreferenceModalProps } from '../types/savePreference';
import { failedToastMessage } from '../utils/commonUtils';
import SavePreferenceForm from './SavePreferenceForm';

export default function SavePreferenceModal({
    open,
    onClose,
    onSave,
    data,
    config,
    isLoading = false,
    initialData = { name: '', description: '', shared: false, pinAsTab: false }
}: SavePreferenceModalProps) {
    const { theme } = useStyling();
    const [formData, setFormData] = useState(initialData);

    useEffect(() => {
        if (open) {
            setFormData({
                ...initialData,
                pinAsTab: initialData.pinAsTab ?? false
            });
        }
        // Reset only when the modal opens. Callers often pass inline initialData
        // objects; depending on initialData would re-run on every parent render
        // and wipe in-progress edits while the modal stays open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSave = async () => {
        const name = formData.name.trim();
        const description = formData.description?.trim() || '';
        const shared = formData.shared !== undefined ? formData.shared : false;
        const pinAsTab = Boolean(formData.pinAsTab);
        try {
            await onSave({ name, description, shared, pinAsTab, data });
            handleClose();
        } catch (error) {
            failedToastMessage(config?.errorMessage);
        }
    };

    const handleClose = () => {
        onClose();
        setFormData(initialData);
    };

    const handleFormDataChange = (newFormData: SavePreferenceFormData) => {
        setFormData(newFormData);
    };

    return (
        <FormModal
            id={config.modalId}
            title={<I18n token={config.title} />}
            titleSx={{ fontWeight: theme.typography.fontWeightBold }}
            isOpen={open}
            onClose={handleClose}
            onSave={handleSave}
            isSaving={isLoading}
            isSaveDisabled={isLoading || !formData?.name?.trim()}
            isReqFields={!formData?.name?.trim()}
            additionalActions={[{ label: <I18n token="dialog.cancel" />, onClick: handleClose }]}
        >
            <SavePreferenceForm config={config} formData={formData} onFormDataChange={handleFormDataChange} />
        </FormModal>
    );
}
